import * as amplitude from '@amplitude/analytics-browser'
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react'
import * as Sentry from '@sentry/remix'
import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import Backend from 'i18next-http-backend'
import { startTransition, StrictMode, useEffect } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { I18nextProvider, initReactI18next } from 'react-i18next'
import { getInitialNamespaces } from 'remix-i18next'
import { initializeExperimentClient } from './services/analytics/experiments'
import { getClientEnvironment } from './config/environment'
import { initializeDatadogClient } from './datadog/datadog.client'
import { isDatadogClientConfig } from './datadog/datadog.config'
import i18nConfig from './i18n-config' // your i18n configuration file
import packageJson from '../package.json'

const clientEnvironment = getClientEnvironment()

console.log(`Client Area v${packageJson.version}`)

if (clientEnvironment === 'production' || clientEnvironment === 'staging') {
  Sentry.init({
    dsn: 'https://7ea30deb7952d02b6008be10d1d753d1@o4505952668024832.ingest.sentry.io/4505991100235776',
    environment: clientEnvironment,
    integrations: [
      Sentry.browserTracingIntegration({ useEffect, useLocation, useMatches }),
      // Replay is only available in the client
      Sentry.replayIntegration(),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      let ignore = false

      if (event.exception?.values && event.exception.values[0]) {
        event.exception?.values[0]?.stacktrace?.frames?.forEach((frame) => {
          // GTM
          if (frame.filename?.includes('gtm.js')) ignore = true
          // Cloudflare
          if (frame.filename?.includes('cloudflare')) ignore = true
        })
      }

      return ignore ? null : event
    },
  })
}

// Initialize Datadog RUM (parallel with Sentry)
const isDatadogEnabled = (window as any).ENV?.DATADOG_ENABLED === true
const datadogConfig = (window as any)?.ENV?.DATADOG_CONFIG

if (isDatadogEnabled && clientEnvironment !== 'local' && isDatadogClientConfig(datadogConfig)) {
  initializeDatadogClient(datadogConfig)
}

const amplitudeApiKey = (window as any).ENV?.AMPLITUDE_API_KEY
const ttpSessionId = document.cookie
  .split('; ')
  .find((row) => row.startsWith('ttp-session-id'))
  ?.split('=')[1]
const ttpDeviceId = document.cookie
  .split('; ')
  .find((row) => row.startsWith('ttp-device-id'))
  ?.split('=')[1]
if (amplitudeApiKey && ttpSessionId && ttpDeviceId) {
  amplitude.init(amplitudeApiKey, {
    autocapture: false,
    // logLevel: getEnvironment() === 'local' ? amplitude.Types.LogLevel.Debug : amplitude.Types.LogLevel.None,
    serverZone: 'EU',
    sessionId: Number(ttpSessionId),
    deviceId: ttpDeviceId,
  })
  initializeExperimentClient()
}

const hydrate = () => {
  i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .use(Backend) // Setup your backend
    .init({
      ...i18nConfig, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      ns: getInitialNamespaces(),
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
        // Disable cache for translation files in development
        requestOptions: { cache: process.env.NODE_ENV === 'production' ? 'default' : 'no-cache' },
      },
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ['htmlTag'],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
    })
    .then(() => {
      startTransition(() => {
        hydrateRoot(
          document,
          <I18nextProvider i18n={i18next}>
            <StrictMode>
              <RemixBrowser />
            </StrictMode>
          </I18nextProvider>,
        )
      })
    })
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate)
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1)
}

// import { RemixBrowser } from '@remix-run/react'
// import { startTransition, StrictMode } from 'react'
// import { hydrateRoot } from 'react-dom/client'
// import { ThemeProvider } from '@material-tailwind/react'
//
// function hydrate() {
//   startTransition(() => {
//     hydrateRoot(
//       document,
//       <StrictMode>
//         <ThemeProvider>
//           <RemixBrowser />
//         </ThemeProvider>
//       </StrictMode>
//     )
//   })
// }
//
// if (window.requestIdleCallback) {
//   window.requestIdleCallback(hydrate)
// } else {
//   // Safari doesn't support requestIdleCallback
//   // https://caniuse.com/requestidlecallback
//   window.setTimeout(hydrate, 1)
// }
